import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear, startOfQuarter, endOfQuarter } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

export interface DateRange {
  from: Date;
  to: Date;
}

interface DateFilterProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  onPresetChange?: (preset: string) => void;
}

const presets = [
  { label: "Últimos 7 días", value: "7d" },
  { label: "Últimos 30 días", value: "30d" },
  { label: "Este mes", value: "this-month" },
  { label: "Mes anterior", value: "last-month" },
  { label: "Este trimestre", value: "this-quarter" },
  { label: "Trimestre anterior", value: "last-quarter" },
  { label: "Este año", value: "this-year" },
  { label: "Personalizado", value: "custom" },
];

export function DateFilter({ dateRange, onDateRangeChange, onPresetChange }: DateFilterProps) {
  const [preset, setPreset] = useState("this-month");
  const [isCustomOpen, setIsCustomOpen] = useState(false);

  const handlePresetChange = (value: string) => {
    setPreset(value);
    onPresetChange?.(value);
    const now = new Date();

    switch (value) {
      case "7d":
        onDateRangeChange({
          from: subDays(now, 7),
          to: now,
        });
        break;
      case "30d":
        onDateRangeChange({
          from: subDays(now, 30),
          to: now,
        });
        break;
      case "this-month":
        onDateRangeChange({
          from: startOfMonth(now),
          to: endOfMonth(now),
        });
        break;
      case "last-month":
        const lastMonth = subMonths(now, 1);
        onDateRangeChange({
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        });
        break;
      case "this-quarter":
        onDateRangeChange({
          from: startOfQuarter(now),
          to: endOfQuarter(now),
        });
        break;
      case "last-quarter":
        const lastQuarterDate = subMonths(now, 3);
        onDateRangeChange({
          from: startOfQuarter(lastQuarterDate),
          to: endOfQuarter(lastQuarterDate),
        });
        break;
      case "this-year":
        onDateRangeChange({
          from: startOfYear(now),
          to: now,
        });
        break;
      case "custom":
        setIsCustomOpen(true);
        break;
    }
  };

  return (
    <div className="flex items-center gap-3">
      <Select value={preset} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-44 bg-secondary border-border">
          <SelectValue placeholder="Seleccionar período" />
        </SelectTrigger>
        <SelectContent className="bg-popover border-border">
          {presets.map((p) => (
            <SelectItem key={p.value} value={p.value}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {preset === "custom" && (
        <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal bg-secondary border-border",
                !dateRange && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, "dd/MM/yyyy", { locale: es })} -{" "}
                    {format(dateRange.to, "dd/MM/yyyy", { locale: es })}
                  </>
                ) : (
                  format(dateRange.from, "dd/MM/yyyy", { locale: es })
                )
              ) : (
                <span>Seleccionar fechas</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-popover border-border" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={dateRange?.from}
              selected={{ from: dateRange.from, to: dateRange.to }}
              onSelect={(range) => {
                if (range?.from && range?.to) {
                  onDateRangeChange({ from: range.from, to: range.to });
                }
              }}
              numberOfMonths={2}
              locale={es}
            />
          </PopoverContent>
        </Popover>
      )}

      <div className="text-sm text-muted-foreground">
        {format(dateRange.from, "dd MMM yyyy", { locale: es })} -{" "}
        {format(dateRange.to, "dd MMM yyyy", { locale: es })}
      </div>
    </div>
  );
}
